import { useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/react";
import { getMyMeetsPast as getMyMeetsPastApi } from "@/api/meets";
import PastMeetsCard from "../cards/past-meets-card";
import { CalendarCheck } from "lucide-react";
import { Skeleton } from "@heroui/react";
import { formatDateRelativePast } from "@/utils/dates";
import PulledDownWrapper from "../wrappers/pulled-down-wrapper";
import MeetingCard from "../v2/meeting-card";
import MeetingCardSkeleton from "../v2/meeting-card-skeleton";
import NoMeetsComp from "./no-meets-comp";
import { matchesMeetingSearch } from "@/utils/meeting-search";

const PastMeetsComp = ({ setNumMeetings, searchQuery }) => {
  const { session } = useSession();
  const [records, setRecord] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    getMyMeetsPast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRecords = useMemo(
    () => records.filter((rec) => matchesMeetingSearch(rec, searchQuery)),
    [records, searchQuery],
  );

  useEffect(() => {
    setNumMeetings(filteredRecords.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRecords]);

  async function getMyMeetsPast() {
    setIsLoading(true);
    setFetchError(false);
    const token = await session.getToken();
    getMyMeetsPastApi(token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setRecord(_resp.data.Records);
        } else {
          throw new Error("Failed to load past meetings");
        }
      })
      .catch((_resp) => {
        console.error(_resp);
        setFetchError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const groups = useMemo(() => {
    const map = new Map();
    for (const rec of filteredRecords) {
      const pretty = formatDateRelativePast(rec.meeting_date);
      if (!map.has(pretty)) {
        map.set(pretty, { key: rec.meeting_date, pretty, items: [] });
      }
      map.get(pretty).items.push(rec);
    }
    return Array.from(map.values());
  }, [filteredRecords]);

  return (
    <PulledDownWrapper onRefresh={getMyMeetsPast}>
      <div className="max-w-[900px] grid grid-cols-12 gap-2">
        {isLoading ? (
          <>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="col-span-12 flex flex-col mb-2">
                <MeetingCardSkeleton />
              </div>
            ))}
          </>
        ) : fetchError ? (
          <div className="col-span-12 flex flex-col items-center gap-3 py-10 text-center text-glass-foreground">
            <p className="font-semibold">Something went wrong!</p>
            <p className="text-sm text-glass-foreground-muted">We couldn't find your meetings.</p>
            <button className="text-sm underline" onClick={getMyMeetsPast}>
              Try again
            </button>
          </div>
        ) : groups.length > 0 ? (
          groups.map((group, gi) => (
            <div key={group.key} className="contents">
              <div className={`col-span-12 ${gi === 0 ? "mt-0" : ""}`}>
                <div className="text-start uppercase text-[10px] font-bold pb-2 tracking-[1.02px] text-glass-foreground">
                  {group.pretty}
                </div>
              </div>

              {group.items.map((rec, ri) => {
                const isLastGroup = gi === groups.length - 1;
                const isLastCard = ri === group.items.length - 1;
                const mb = isLastGroup && isLastCard ? "mb-20" : "mb-2";
                return (
                  <div key={rec._id} className={`col-span-12 flex flex-col ${mb}`}>
                    <MeetingCard _record={rec} _onResp={null} />
                  </div>
                );
              })}
            </div>
          ))
        ) : (
          <NoMeetsComp title={searchQuery ? "No matching Loopps" : "No Past Loopps Yet"} />
        )}
      </div>
    </PulledDownWrapper>
  );
};

export default PastMeetsComp;
